import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import { removeAllTokens } from '../utils/storage';
import { ACCESS_TOKEN, REFRESH_TOKEN, PROVIDER_ID } from '../store/domains/auth/constants';

import { BASE_URL } from '../constants';

const MySwal = withReactContent(Swal);

export const headers = {
  'Content-type': 'application/json',
  'Content-Language': 'AZ',
  Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN)}`,
  'Provider-Id': localStorage.getItem(PROVIDER_ID),
};

const Axios = axios.create({
  baseURL: BASE_URL,
  headers,
});

const withoutAuthHeaderPaths = ['/iam/auth/sign', '/iam/auth/verify', '/iam/auth/refresh'];

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
// Queue of failed requests to retry after token refresh
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }
  const response = await axios.put(`${BASE_URL}/iam/auth/refresh`, {
    refreshToken,
  });
  return response.data;
};

Axios.interceptors.request.use((config) => {
  // Always get fresh token from localStorage for each request
  const token = localStorage.getItem(ACCESS_TOKEN);
  if (token && !withoutAuthHeaderPaths.includes(config.url || '')) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (withoutAuthHeaderPaths.includes(config.url || '')) {
    delete config?.headers?.Authorization;
  }
  return config;
});

Axios.interceptors.response.use(
  (response) => {
    console.log('RESPONSE: ', response);
    if (response?.data?.data?.accessToken) {
      localStorage.setItem(ACCESS_TOKEN, response.data.data.accessToken);
      if (response?.data?.data?.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN, response.data.data.refreshToken);
      }
      Axios.defaults.headers.Authorization = `Bearer ${response?.data.data.accessToken}`;
      Axios.defaults.headers['Provider-Id'] = response?.data.data?.userInfo?.providerIds[0];
    }
    return response;
  },
  async (error) => {
    console.log('INTERCEPTOR: ', error);
    const originalRequest = error.config;

    // 401 means unauthorized - redirect to login immediately
    if (error?.response?.status === 401 && !window.location.pathname.includes('/auth')) {
      removeAllTokens();
      window.location.assign('/login');
      return Promise.reject(error);
    }

    // 403 means access token expired - try to refresh.
    // Exclude the auth endpoints: a 403 from sign/verify/refresh is always a
    // genuine business error (e.g. an ineligible role or a bad OTP), never an
    // expired token, so it must fall through to the error modal below instead
    // of triggering a doomed refresh that hides the real message.
    if (
      error?.response?.status === 403 &&
      !window.location.pathname.includes('/auth') &&
      !withoutAuthHeaderPaths.includes(originalRequest?.url || '') &&
      !originalRequest._retry
    ) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return Axios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResponse = await refreshAccessToken();
        const newAccessToken = refreshResponse?.data?.accessToken;
        const newRefreshToken = refreshResponse?.data?.refreshToken;

        if (newAccessToken) {
          localStorage.setItem(ACCESS_TOKEN, newAccessToken);
          if (newRefreshToken) {
            localStorage.setItem(REFRESH_TOKEN, newRefreshToken);
          }
          Axios.defaults.headers.Authorization = `Bearer ${newAccessToken}`;
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          processQueue(null, newAccessToken);
          return Axios(originalRequest);
        }
        throw new Error('Failed to refresh token');
      } catch (refreshError) {
        processQueue(refreshError, null);
        removeAllTokens();
        window.location.assign('/login');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const errorResponse = {
      ...error?.response?.data,
    };

    // The backend wraps role/eligibility failures in a nested payload where the
    // outer `message` is a generic "Fail" and the meaningful, user-friendly text
    // lives in `data.message`. Prefer the nested message, falling back to the
    // outer one for endpoints that return a flat `{ message }`. RFC 9457
    // ProblemDetail responses (stock-management) carry the text in `detail`.
    const displayMessage = errorResponse?.data?.message || errorResponse?.message || errorResponse?.detail;
    const failedConstraints = errorResponse?.failed_constraints;
    const correlationId = errorResponse?.correlation_id;

    MySwal.fire({
      title: <p>{displayMessage}</p>,
      html: (failedConstraints?.length || correlationId) && (
        <>
          {failedConstraints?.map((constraint) => (
            <div key={constraint}>{constraint}</div>
          ))}
          {correlationId && <small>Correlation ID: {correlationId}</small>}
        </>
      ),
      icon: 'error',
    });
    return Promise.reject(errorResponse || error);
  }
);

export default Axios;
